// Zoom thresholds for when map data appears. Defaults suit production; each
// can be overridden per-session via URL query params for benchmarking, e.g.
//   /?zRoutes=6&zTracks=8&zLabels=7&zPins=4
// Overrides are read once at page load and survive panning because MapPage
// rewrites camera params with URLSearchParams(prev), preserving the rest.
const DEFAULTS = {
  /** Route pins + route data fetching. */
  routes: 9,
  /** Track geometries (mainGeom) on routes. */
  tracks: 11,
  /** Place name labels (MapLibre layer minzoom). */
  labels: 10,
  /**
   * Floor for fetching place/outing pins at all. Clustering makes the render
   * readable well below the route threshold, but the server still has to
   * answer the bbox query — below zoom 5 a viewport covers a continent and
   * the budgeted sample stops being representative, so we keep the
   * zoom-in hint instead of fetching the whole world.
   */
  pins: 5,
};

function readOverrides(): typeof DEFAULTS {
  if (typeof window === "undefined") return DEFAULTS;
  const params = new URLSearchParams(window.location.search);
  const num = (key: string, fallback: number) => {
    const raw = params.get(key);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : fallback;
  };
  const zoom = {
    routes: num("zRoutes", DEFAULTS.routes),
    tracks: num("zTracks", DEFAULTS.tracks),
    labels: num("zLabels", DEFAULTS.labels),
    pins: num("zPins", DEFAULTS.pins),
  };
  const overridden = Object.entries(zoom).filter(([k, v]) => v !== DEFAULTS[k as keyof typeof DEFAULTS]);
  if (overridden.length) {
    console.info("[gaea] zoom threshold overrides:", Object.fromEntries(overridden));
  }
  return zoom;
}

export const ZOOM_THRESHOLDS = readOverrides();

export const ROUTES_MIN_ZOOM = ZOOM_THRESHOLDS.routes;
export const TRACKS_MIN_ZOOM = ZOOM_THRESHOLDS.tracks;
export const PLACE_LABELS_MIN_ZOOM = ZOOM_THRESHOLDS.labels;
export const PINS_MIN_ZOOM = ZOOM_THRESHOLDS.pins;

/**
 * Rows per request. Must stay at or below pdbq's configured max page size
 * (500) — exceeding it is a compile error on the query, not a clamp.
 *
 * 500 is where a full viewport still completes in one concurrency wave: each
 * request costs ~58ms fixed + ~0.18ms/row, so larger pages stop paying for
 * themselves and only delay the first paint (progressive rendering means page
 * one is what the user actually waits for).
 */
export const PAGE_SIZE = 500;

/**
 * Page size for the routes layer. Attaching `mainGeom` costs roughly 5x per
 * row (~0.62ms vs ~0.21ms measured), so a full 500-row page takes ~308ms —
 * too long for a first paint when that page is what the user waits to see.
 * Smaller pages when tracks are on trade a couple of extra requests, which
 * run concurrently anyway, for a much faster initial render.
 */
export const ROUTES_PAGE_SIZE = { withTracks: 150, withoutTracks: PAGE_SIZE };

/**
 * Ceiling on how many features a layer will pull for one viewport, by zoom.
 * Now that the bbox filter runs against the full tables rather than the old
 * LIMIT-capped SQL functions, a wide viewport can legitimately match tens of
 * thousands of routes — more than MapLibre can draw usefully. Budgets stay
 * small when zoomed out (where individual features are unreadable anyway) and
 * open up as the viewport tightens and totalCount naturally falls.
 *
 * Pagination stops at the budget, and the caller reports "showing N of M" so
 * truncation is never silent.
 */
function budgetForZoom(zoom: number, wideOpen: number, tight: number): number {
  if (zoom >= TRACKS_MIN_ZOOM + 2) return tight;
  if (zoom >= TRACKS_MIN_ZOOM) return Math.round(tight / 2);
  return wideOpen;
}

export const featureBudget = {
  /**
   * Point pins are cheap, and at low zoom they cluster instead of smearing —
   * the wide-open budget is generous (2000 ≈ 4 concurrent pages) so cluster
   * counts approximate reality, without letting a world-spanning viewport
   * pull an unbounded set.
   */
  places: (zoom: number) => budgetForZoom(zoom, 2000, 4000),
  /**
   * Routes are the expensive layer once mainGeom (a simplified LineString per
   * route) is attached, so the budget is tighter when tracks are drawn.
   */
  routes: (zoom: number, withTracks: boolean) =>
    withTracks ? budgetForZoom(zoom, 250, 1500) : budgetForZoom(zoom, 500, 3000),
  /** Region polygons are few but heavy; the table itself is small. */
  regions: (zoom: number) => budgetForZoom(zoom, 100, 400),
  /** Clustered at low zoom like places, so the same reasoning applies. */
  outings: (zoom: number) => budgetForZoom(zoom, 1000, 2000),
  /** Upcoming events with a meeting point are few; same clustering as outings. */
  events: (zoom: number) => budgetForZoom(zoom, 1000, 2000),
};
